"use client";

import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  FileText,
  ClipboardList,
  Info,
  UserRound,
  Bird,
  ExternalLink,
} from "lucide-react";
import ExternalLinkModal from "@/components/ExternalLinkModal/ExternalLinkModal";

const HERO_IMAGE =
  "https://galeria.tocumenaero.com/gallery/aeropuerto/aeropuerto080.jpg";

const REPORTES_URL = "https://reportes.tocumenaero.com/reportes/create-ivi";

// Correo y teléfono son iguales en todos los idiomas, por eso no van en i18n
const CORREO = "peligroaviario@tocumenpanama.aero";
const TELEFONO = "238-2807";

function Bloque({
  icon: Icono,
  titulo,
  className = "",
  children,
}: {
  icon: React.ElementType;
  titulo: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`bg-white border border-gray-200 rounded-2xl p-6 shadow-sm ${className}`}
    >
      <h2 className="flex items-center gap-3 mb-5">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#102542]/5 text-[#102542] shrink-0">
          <Icono className="w-4 h-4" />
        </span>
        <span className="text-xs uppercase tracking-wider font-bold text-[#102542]">
          {titulo}
        </span>
      </h2>
      {children}
    </div>
  );
}

function Vinetas({
  items,
  className = "space-y-2.5",
}: {
  items: string[];
  className?: string;
}) {
  return (
    <ul className={className}>
      {items.map((item) => (
        <li
          key={item}
          className="flex gap-3 text-sm text-gray-600 leading-relaxed"
        >
          <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-[#102542]/40 shrink-0" />
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function FaunaPage() {
  const t = useTranslations("faunaPage");
  const [modalAbierto, setModalAbierto] = useState(false);

  const handleContinue = () => {
    window.open(REPORTES_URL, "_blank", "noopener,noreferrer");
    setModalAbierto(false);
  };

  return (
    <main className="min-h-screen bg-white">
      {/* Hero con imagen */}
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <Image
          src={HERO_IMAGE}
          alt={t("title")}
          fill
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
        <div className="relative z-10 max-w-[80%] mx-auto w-full pb-16 md:pb-20">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
              <span className="w-8 h-px bg-white" />
              {t("eyebrow")}
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight text-balance">
              {t("title")}
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              {t("subtitle")}
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-[80%] mx-auto py-16">
        {/* Introducción y acceso al formulario de reporte */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 pb-8 border-b border-gray-200">
          <div className="max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight text-balance">
              {t("intro.title")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t("intro.description")}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setModalAbierto(true)}
            className="shrink-0 self-start inline-flex items-center gap-2 px-6 py-3 bg-[#102542] text-white rounded-full text-xs uppercase tracking-wider font-semibold hover:bg-[#0a1a30] transition-colors cursor-pointer"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            {t("reportButton")}
          </button>
        </div>

        {/* items-start: sin esto el grid estira cada tarjeta a la altura de la
            más alta de su fila y las cortas quedan medio vacías */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <Bloque icon={FileText} titulo={t("documentacionLegal.title")}>
            <Vinetas items={t.raw("documentacionLegal.items") as string[]} />
          </Bloque>

          <Bloque icon={ClipboardList} titulo={t("quienesReportan.title")}>
            <div className="flex flex-wrap gap-2">
              {(t.raw("quienesReportan.items") as string[]).map((quien) => (
                <span
                  key={quien}
                  className="px-3 py-1.5 rounded-full bg-[#102542]/5 border border-[#102542]/15 text-xs font-semibold text-[#102542]"
                >
                  {quien}
                </span>
              ))}
            </div>
          </Bloque>

          <Bloque
            icon={Bird}
            titulo={t("acciones.title")}
            className="md:col-span-2"
          >
            <p className="text-sm text-gray-600 leading-relaxed mb-5">
              {t("acciones.description")}
            </p>
            <Vinetas
              items={t.raw("acciones.items") as string[]}
              className="grid gap-2.5 sm:grid-cols-2"
            />
          </Bloque>

          <Bloque icon={UserRound} titulo={t("contacto.title")}>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                <a
                  href={`mailto:${CORREO}`}
                  className="text-gray-600 hover:text-[#102542] transition-colors break-all"
                >
                  {CORREO}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                <a
                  href={`tel:${TELEFONO.replace(/[^\d+]/g, "")}`}
                  className="text-gray-600 hover:text-[#102542] transition-colors tabular-nums"
                >
                  {TELEFONO}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                <span className="text-gray-600 leading-relaxed">
                  {t("contacto.ubicacion")}
                </span>
              </li>
            </ul>
          </Bloque>

          <Bloque icon={Clock} titulo={t("horarios.title")}>
            <dl className="space-y-4">
              <div>
                <dt className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">
                  {t("horarios.administrativosLabel")}
                </dt>
                <dd className="text-sm font-semibold text-gray-900 tabular-nums">
                  {t("horarios.administrativos")}
                </dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">
                  {t("horarios.turnoRotativoLabel")}
                </dt>
                <dd className="text-sm font-semibold text-gray-900 tabular-nums">
                  {t("horarios.turnoRotativo")}
                </dd>
              </div>
            </dl>
          </Bloque>

          <Bloque
            icon={Info}
            titulo={t("observaciones.title")}
            className="md:col-span-2"
          >
            <p className="text-sm text-gray-600 leading-relaxed">
              {t("observaciones.text")}
            </p>
          </Bloque>
        </div>
      </section>

      <ExternalLinkModal
        isOpen={modalAbierto}
        onClose={() => setModalAbierto(false)}
        onContinue={handleContinue}
      />
    </main>
  );
}
